import { css, Dialog } from "@mui/material";
import { useAtom } from "jotai";
import { useRouter } from "next/router";
import React, { useEffect } from "react";

import SpotlightPopupPane from "@/components/domain/spotlight/dialog/SpotlightPopupPane";
import AccountInfoSettingPane from "@/components/provider/dialog/AccountInfoSettingPane";
import SnsAccountsSettingPane from "@/components/provider/dialog/SnsAccountsSettingPane";
import SnsProRecommendationPane from "@/components/provider/dialog/SnsProRecommendationPane";
import { useGlobalDialog } from "@/custom-hooks/jotai/use-global-dialog";
import { useSpotlightState } from "@/custom-hooks/jotai/use-spotlight-state";
import { authUserAtom } from "@/global-state/jotai-atom";

type Props = {
  children: React.ReactNode;
};

const GlobalDialogProvider: React.FC<Props> = (props) => {
  const router = useRouter();

  const [authUser] = useAtom(authUserAtom);
  const { popupSpotlight } = useSpotlightState();
  const { currentDialogType, updateDialogType, closeDialog } = useGlobalDialog();

  useEffect(() => {
    (async () => {
      await updateDialogType();
    })();
  }, [router.pathname, authUser?.snsAccounts, popupSpotlight?.id]);

  const DIALOG_PANES = {
    NONE: {
      class: undefined,
      component: <></>,
      number: 0,
    },
    ACCOUNT_INFO_SETTING: {
      class: undefined,
      component: <AccountInfoSettingPane />,
      number: 1,
    },
    SNS_ACCOUNT_SETTING: {
      class: undefined,
      component: <SnsAccountsSettingPane />,
      number: 2,
    },
    SNS_PRO_RECOMMENDATION: {
      class: undefined,
      component: <SnsProRecommendationPane />,
      number: 3,
    },
    SPOTLIGHT_POPUP: {
      class: "box-shadow-none",
      component: <SpotlightPopupPane />,
      number: 4,
    },
  };

  const dialogPaneObj = DIALOG_PANES[currentDialogType];

  return (
    <>
      <Dialog
        fullWidth
        maxWidth={"sm"}
        onClose={(_event: object, reason: "backdropClick" | "escapeKeyDown") => {
          if (reason === "backdropClick") {
            closeDialog();
            updateDialogType(dialogPaneObj.number);
          }
        }}
        open={currentDialogType !== "NONE"}
        className={dialogPaneObj.class}
        css={style.dialog}
      >
        {dialogPaneObj.component}
      </Dialog>
      {props.children}
    </>
  );
};

export default GlobalDialogProvider;

const style = {
  dialog: css`
    &.box-shadow-none {
      .MuiDialog-paper {
        background-color: transparent;
        box-shadow: none;
      }
    }
  `,
};
